// Shared bias utility functions - can be used by both server and client components

export function getSubtopicBiasLabel(articles) {
  let left = 0, center = 0, right = 0
  articles.forEach(article => {
    const bias = article.outlet?.bias ?? 0
    if (bias < -6) left++
    else if (bias > 6) right++
    else center++
  })
  const total = articles.length
  if (total === 0) return { text: 'No sources', color: 'text-muted-foreground' }

  const leftPct = (left / total) * 100
  const centerPct = (center / total) * 100
  const rightPct = (right / total) * 100

  // Polarized: mostly left + right, few center (<20%)
  if (centerPct < 20 && leftPct > 25 && rightPct > 25) {
    return { text: 'Polarized', color: 'text-purple-600 dark:text-purple-400' }
  }

  // Left-Center: minimal right coverage (<15%)
  if (rightPct < 15 && leftPct > 20 && centerPct > 20) {
    return { text: 'Left-Center', color: 'text-blue-500 dark:text-blue-400' }
  }

  // Center-Right: minimal left coverage (<15%)
  if (leftPct < 15 && rightPct > 20 && centerPct > 20) {
    return { text: 'Center-Right', color: 'text-red-500 dark:text-red-400' }
  }

  // Dominated by one side (>60%)
  if (leftPct >= 60) {
    return { text: `${Math.round(leftPct)}% Left`, color: 'text-blue-600 dark:text-blue-400' }
  }
  if (rightPct >= 60) {
    return { text: `${Math.round(rightPct)}% Right`, color: 'text-red-600 dark:text-red-400' }
  }
  if (centerPct >= 60) {
    return { text: 'Mostly Center', color: 'text-muted-foreground' }
  }

  // Good mix of all three
  return { text: 'Balanced', color: 'text-green-600 dark:text-green-400' }
}

export function getBiasLabel(bias) {
  if (bias <= -18) return { label: 'Far Left', color: 'text-blue-800 dark:text-blue-300', bg: 'bg-blue-200 dark:bg-blue-900/50' }
  if (bias <= -12) return { label: 'Left', color: 'text-blue-600 dark:text-blue-400', bg: 'bg-blue-100 dark:bg-blue-900/30' }
  if (bias < -6) return { label: 'Leans Left', color: 'text-blue-500 dark:text-blue-400', bg: 'bg-blue-50 dark:bg-blue-900/20' }
  if (bias <= 6) return { label: 'Center', color: 'text-muted-foreground', bg: 'bg-muted' }
  if (bias < 12) return { label: 'Leans Right', color: 'text-red-500 dark:text-red-400', bg: 'bg-red-50 dark:bg-red-900/20' }
  if (bias < 18) return { label: 'Right', color: 'text-red-600 dark:text-red-400', bg: 'bg-red-100 dark:bg-red-900/30' }
  return { label: 'Far Right', color: 'text-red-800 dark:text-red-300', bg: 'bg-red-200 dark:bg-red-900/50' }
}

// Simplified 3-category version for filtering
export function getBiasCategory(bias) {
  if (bias < -6) return 'left'
  if (bias > 6) return 'right'
  return 'center'
}

export function getReliabilityLabel(reliability) {
  if (reliability >= 40) return { label: 'Reliable', color: 'text-green-600 dark:text-green-400' }
  if (reliability >= 32) return { label: 'Mostly Reliable', color: 'text-emerald-600 dark:text-emerald-400' }
  if (reliability >= 24) return { label: 'Mixed Reliability', color: 'text-yellow-600 dark:text-yellow-400' }
  return { label: 'Low Reliability', color: 'text-red-600 dark:text-red-400' }
}

export function getReliabilityInfo(reliability) {
  if (reliability >= 40) return {
    label: 'Reliable',
    description: 'This outlet is considered highly reliable with strong fact-checking standards.'
  }
  if (reliability >= 32) return {
    label: 'Mostly Reliable',
    description: 'This outlet is generally reliable but may occasionally contain minor errors.'
  }
  if (reliability >= 24) return {
    label: 'Mixed Reliability',
    description: 'This outlet has mixed reliability and should be read with some caution.'
  }
  return {
    label: 'Low Reliability',
    description: 'This outlet has lower reliability standards. Consider verifying information elsewhere.'
  }
}
